// Profile hub (§13.1–§13.2): photo, name, product, application reference,
// last login, KYC / mobile-banking state and the profile menu.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCamera,
  faChevronRight,
  faFileInvoice,
  faGear,
  faBuildingColumns,
  faIdCard,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { getProfile, setProfilePhoto } from '@/api/profile';
import type { RootState } from '@/store';
import type { ProfileInfo } from '@/types/phase2.types';
import { formatLong } from '@/mock/clock';
import Loader from '@/components/ui/Loader/Loader';
import './profile.css';

const MAX_PHOTO_BYTES = 2 * 1024 * 1024;

const KYC_LABELS: Record<ProfileInfo['kycState'], string> = {
  FULL: 'KYC complete',
  LIMITED: 'Limited KYC',
  PENDING: 'KYC pending',
};

const ProfileHome = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.account.user);
  const selectedAccount = useSelector((state: RootState) => state.account.selectedAccount);
  const [profile, setProfile] = useState<ProfileInfo | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getProfile({
      name: user?.fullName,
      email: user?.email,
      product: selectedAccount?.accountType,
      applicationRef: user?.customerId,
    })
      .then(setProfile)
      .catch(() => setProfile(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickPhoto = () => fileRef.current?.click();

  const handlePhotoFile = (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error('Pick a photo under 2 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        const dataUrl = String(reader.result);
        await setProfilePhoto(dataUrl);
        setProfile((p) => (p ? { ...p, photoDataUrl: dataUrl } : p));
        toast.success('Profile photo updated');
      } catch {
        toast.error('Could not update the photo. Try again.');
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = async () => {
    setUploading(true);
    try {
      await setProfilePhoto(null);
      setProfile((p) => (p ? { ...p, photoDataUrl: undefined } : p));
      toast.success('Profile photo removed');
    } catch {
      toast.error('Could not remove the photo. Try again.');
    } finally {
      setUploading(false);
    }
  };

  const menu = [
    { icon: faIdCard, title: 'Personal details', sub: 'Name, email and application reference', to: '/profile/details' },
    { icon: faUsers, title: 'Nominees', sub: 'Who gets the money, and in what share', to: '/profile/nominees' },
    { icon: faBuildingColumns, title: 'Bank Account', sub: 'Cheques, lockers, disputes and account services', to: '/profile/bank' },
    { icon: faFileInvoice, title: 'Statement', sub: 'Every credit and debit, with downloads', to: '/statement' },
    { icon: faGear, title: 'App settings', sub: 'Alerts, MPIN, password and de-register', to: '/profile/settings' },
  ];

  return (
    <div className="nova-profile-page">
      {!profile && <Loader rows={4} />}
      {profile && (
        <>
          <div className="nova-profile-header">
            <div className="nova-profile-photo">
              {profile.photoDataUrl ? (
                <img src={profile.photoDataUrl} alt="Profile" />
              ) : (
                profile.name.trim().charAt(0).toUpperCase()
              )}
              <button
                type="button"
                className="nova-profile-photo-edit"
                title={profile.photoDataUrl ? 'Change photo' : 'Add photo'}
                aria-label={profile.photoDataUrl ? 'Change photo' : 'Add photo'}
                disabled={uploading}
                onClick={pickPhoto}
              >
                <FontAwesomeIcon icon={faCamera} />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handlePhotoFile(e.target.files?.[0])}
              />
            </div>
            <div className="nova-profile-head-text">
              <h2>{profile.name}</h2>
              <p>
                {profile.product} · Ref {profile.applicationRef}
              </p>
              <p>
                {profile.firstLogin
                  ? 'This is your first login'
                  : `Last login ${profile.lastLoginAt ? formatLong(profile.lastLoginAt) : ''}`}
              </p>
              <div className="nova-profile-chips">
                <span className="nova-profile-chip">{KYC_LABELS[profile.kycState]}</span>
                {!profile.mobileBanking && <span className="nova-profile-chip">Mobile banking not set up</span>}
                {profile.photoDataUrl && (
                  <button type="button" className="nova-profile-chip btn p-0 px-2 border-0" onClick={() => void removePhoto()}>
                    Remove photo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="nova-profile-menu">
            {menu.map((item) => (
              <button
                type="button"
                key={item.to}
                className="nova-profile-menu-row"
                onClick={() => navigate(item.to)}
              >
                <span className="nova-profile-menu-icon">
                  <FontAwesomeIcon icon={item.icon} />
                </span>
                <span className="nova-ben-row-text">
                  <span className="nova-ben-alias">{item.title}</span>
                  <span className="nova-ben-count">{item.sub}</span>
                </span>
                <FontAwesomeIcon icon={faChevronRight} className="nova-pay-chevron" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ProfileHome;
